/**
 * Every root path the CRM router can actually resolve.
 *
 * Two consumers depend on this being exhaustive:
 *  - the tenant-prefix guards, which treat an unrecognised first segment as a
 *    tenant slug (e.g. `/acme/cases` -> `/cases`);
 *  - `CrmAuthSessionService.isProtectedRoute`, which refuses to persist or
 *    restore a path that is not listed here.
 *
 * Keep it in sync when adding a top-level route in `app.routes.ts`.
 */
export const APP_ROOT_PATHS: ReadonlySet<string> = new Set([
  'login',
  'verify',
  'forgot-password',
  'set-password',
  'reset-password',
  'error',
  'manual-tenant',
  'in-review-thank-you',
  'application-under-review',
  'create-hq',
  'profile',
  'branch',
  'company-settings',
  'dashboard',
  'employee-interview-form',
  'user-view-application',
  'user-onboarding',
  'user-application-under-review',
  'introducer-interview-form',
  'registered-individual-interview-form',
  'clients',
  'customers',
  'cases',
  'quick-sourcing',
  'user-management',
  'branch-management',
  'ar-management',
  'commission-management',
  'cpd',
  'exam-zone',
  'my-commission',
  'documents-library',
  'reports',
  'configurations',
  'help-center',
  'notifications',
  'suitability-templates',
]);

const firstSegmentOf = (url: string): string =>
  String(url || '')
    .split(/[?#]/)[0]
    .split('/')
    .filter(Boolean)[0]
    ?.trim()
    .toLowerCase() ?? '';

/** True when `segment` is a top-level route rather than a tenant slug. */
export const isAppRootPath = (segment: string): boolean =>
  APP_ROOT_PATHS.has(String(segment || '').trim().toLowerCase());

/** True when `url`'s first segment resolves to a real route (`/cases/12` -> yes, `/garbage` -> no). */
export const isKnownAppUrl = (url: string): boolean => isAppRootPath(firstSegmentOf(url));
